import { ICase } from '@brands/services/cases/types/ICase';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import moment from 'moment-timezone';

export const FALLBACK_AVATAR =
  'https://media.istockphoto.com/id/1130884625/tr/vekt%C3%B6r/kullan%C4%B1c%C4%B1-%C3%BCyesi-vekt%C3%B6r-simgesi-ui-kullan%C4%B1c%C4%B1-arabirimi-veya-profil-face-avatar-uygulamas%C4%B1.jpg?s=612x612&w=0&k=20&c=jAf5nq1ebnZo8TJbjaB9dMHMvgff7uOk67NkF5CpgB0=';

export const resolveCollectorUser = (thisCase: ICase, userInfo: UserProfile): UserProfile | null | undefined => {
  const matchesLoggedInCollector =
    thisCase.collector_id != null && Number(thisCase.collector_id) === Number(userInfo.id);

  if (thisCase.collector?.full_name?.trim()) {
    return thisCase.collector;
  }
  if (matchesLoggedInCollector) {
    return userInfo;
  }
  return thisCase.collector ?? undefined;
};

export const getCollectorDisplayName = (thisCase: ICase, userInfo: UserProfile): string => {
  const name = resolveCollectorUser(thisCase, userInfo)?.full_name?.trim();
  return name || '-';
};

export const formatDateTime = (utcIso: string, timeZone: string): string => {
  // Backend sends naive ISO ("2026-04-22T07:00:00" with no Z), interpreted
  // as UTC. `new Date()` on naive strings varies across runtimes and can
  // read them as local — force UTC parsing to match the rest of the app
  // (e.g. patient dashboard's moment.utc(...).tz(timeZone)).
  const date = moment.utc(utcIso).toDate();

  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  })
    .format(date)
    .replace(/, (\d{1,2}:\d{2} [ap]m)/i, ' at $1');
};

export const svgToBase64 = async (svgUrl: string): Promise<string> => {
  const response = await fetch(svgUrl);
  const svgText = await response.text();

  const base64 = btoa(unescape(encodeURIComponent(svgText)));
  return `data:image/svg+xml;base64,${base64}`;
};

export const convertSvgBase64ToPngBase64 = (svgBase64: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);

      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = reject;
    img.src = svgBase64;
  });

export const formatWaitingDuration = (durationInSeconds: number): string => {
  const minutes = Math.floor(durationInSeconds / 60);

  if (minutes < 1) return 'Waiting for less than a minute';
  if (minutes === 1) return 'Waiting for 1 minute';
  return `Waiting for ${minutes} minutes`;
};

export const getConversationTypeKey = (roleA: string, roleB: string): string | null => {
  const pair = [roleA, roleB].sort();

  if (pair.includes(UserRoleName.Collector) && pair.includes(UserRoleName.Patient)) {
    return `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`;
  }

  if (pair.includes(UserRoleName.Collector) && pair.includes(UserRoleName.Provider)) {
    return `${UserRoleName.Patient}_${UserRoleName.Provider}`;
  }

  if (pair.includes(UserRoleName.Collector) && pair.includes(UserRoleName.CareAdvisor)) {
    return `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`;
  }

  if (pair.includes(UserRoleName.Patient) && pair.includes(UserRoleName.CareAdvisor)) {
    return `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`;
  }

  if (pair.includes(UserRoleName.Patient) && pair.includes(UserRoleName.Provider)) {
    return `${UserRoleName.Patient}_${UserRoleName.Provider}`;
  }

  if (pair.includes(UserRoleName.Provider) && pair.includes(UserRoleName.CareAdvisor)) {
    return `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}`;
  }

  return null;
};

export const getConversationApiType = (conversationType: string): string => {
  switch (conversationType) {
    case `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}`:
      return 'provider_care_advisor_conversation';
    case `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`:
      return 'patient_care_advisor_conversation';
    case `${UserRoleName.Patient}_${UserRoleName.Provider}`:
      return 'patient_provider_conversation';
    default:
      throw new Error(`Unsupported conversation type: ${conversationType}`);
  }
};

export const getConversationSidFromCase = (caseData: ICase, conversationType: string): string | null => {
  switch (conversationType) {
    case `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`:
      return caseData.patient_care_advisor_conversation_sid ?? null;
    case `${UserRoleName.Patient}_${UserRoleName.Provider}`:
      return caseData.patient_provider_conversation_sid ?? null;
    case `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}`:
      return caseData.provider_care_advisor_conversation_sid ?? null;
    case `${UserRoleName.Patient}_${UserRoleName.Collector}`:
      return caseData.patient_collector_conversation_sid ?? null;
    case `${UserRoleName.Provider}_${UserRoleName.Collector}`:
      return caseData.provider_collector_conversation_sid ?? null;
    case `${UserRoleName.CareAdvisor}_${UserRoleName.Collector}`:
      return caseData.care_advisor_collector_conversation_sid ?? null;
    default:
      return null;
  }
};

// A Dependent's case has its own logged-in account (patient.id) as well as a
// guardian account (patient.guardian_id) that also needs conversation access.
// Add both as participants so either account is a real member: collapsing
// this to a single identity locks the other one out of the conversation.
export const getCasePatientParticipantIdentities = (caseData: ICase): string[] => {
  const identities = [caseData.patient?.id, caseData.patient?.guardian_id]
    .filter((id): id is number => id != null)
    .map(String);

  return Array.from(new Set(identities));
};

export const getConversationIncludeOptions = (viewerRoleName: string) => ({
  userRoleName: viewerRoleName,
  include_patient_care_advisor_conversation:
    viewerRoleName !== UserRoleName.Admin &&
    viewerRoleName !== UserRoleName.SuperAdmin &&
    viewerRoleName !== UserRoleName.OrganizationAdmin,
  include_patient_provider_conversation:
    viewerRoleName !== UserRoleName.Admin &&
    viewerRoleName !== UserRoleName.SuperAdmin &&
    viewerRoleName !== UserRoleName.OrganizationAdmin,
  include_provider_care_advisor_conversation:
    viewerRoleName !== UserRoleName.Patient &&
    viewerRoleName !== UserRoleName.Admin &&
    viewerRoleName !== UserRoleName.SuperAdmin &&
    viewerRoleName !== UserRoleName.OrganizationAdmin,
  include_patient_collector_conversation:
    viewerRoleName !== UserRoleName.Admin &&
    viewerRoleName !== UserRoleName.SuperAdmin &&
    viewerRoleName !== UserRoleName.OrganizationAdmin,
  include_provider_collector_conversation:
    viewerRoleName !== UserRoleName.Patient &&
    viewerRoleName !== UserRoleName.Admin &&
    viewerRoleName !== UserRoleName.SuperAdmin &&
    viewerRoleName !== UserRoleName.OrganizationAdmin,
  include_care_advisor_collector_conversation:
    viewerRoleName !== UserRoleName.Patient &&
    viewerRoleName !== UserRoleName.Admin &&
    viewerRoleName !== UserRoleName.SuperAdmin &&
    viewerRoleName !== UserRoleName.OrganizationAdmin,
});

export const getParticipantIdentityForConversation = (
  conversationType: string,
  caseData: ICase,
  myRoleName: string,
): string[] => {
  switch (conversationType) {
    case `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}`:
      if (myRoleName === UserRoleName.CareAdvisor) {
        return [String(caseData.provider_id)];
      }
      return [String(caseData.care_advisor_id)];

    case `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`:
    case `${UserRoleName.Patient}_${UserRoleName.Provider}`:
      if (myRoleName === UserRoleName.Patient || myRoleName === UserRoleName.Dependent) {
        if (caseData.care_advisor_id && conversationType === `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`) {
          return [String(caseData.care_advisor_id)];
        }

        if (caseData.provider_id && conversationType === `${UserRoleName.Patient}_${UserRoleName.Provider}`) {
          return [String(caseData.provider_id)];
        }
      }

      return getCasePatientParticipantIdentities(caseData);

    default:
      throw new Error(`Unsupported conversation type: ${conversationType}`);
  }
};
